import React, { useState } from 'react';
import { icons } from '@ublend-npm/aulaui-next';
import { Transforms } from 'slate';

import {
  ReactEditor,
  useReadOnly,
  useSelected,
  useSlateStatic,
} from 'slate-react';
import {
  BlockLinkContent,
  BlockLinkWrapper,
  Header,
  LtiLinkBlockWrapper,
  LoadingContainer,
  Title,
  ImageWrapper,
} from './LtiLinkBlock.styled';

import { BlockElement } from '../../types';

import { useAulaEditorContext } from '../../EditorContext';
import { Loading } from '../Loading/Loading';
import { BlockToolbar, BlockToolbarItem } from '../BlockToolbar/BlockToolbar';
import {
  BlockButton,
  Failed,
  FailedDescription,
  FailedIcon,
} from '../File/File.styled';

export type LtiLinkBlockProps = {
  /** The slate element for the LTI block */
  element: BlockElement;

  /** The title text that will be used for cases such as error block or collapsed mode */
  title: string;

  /** The icon that will be used for cases such as collapsed mode */
  icon?: string;

  /**
   * LTI consumer endpoint route for the given LTI block.
   * Excludes endpoint origin, because origin depends on configuration.
   * Example: panopto-lti-consumer
   */
  ltiEndpoint: string;
};

export const LtiLinkBlock = ({
  title,
  icon: ltiIcon,
  element,
  ltiEndpoint,
}: LtiLinkBlockProps) => {
  const editor = useSlateStatic();
  const isReadOnly = useReadOnly();
  const isSelected = useSelected();

  const { entities, config, updateEntity } = useAulaEditorContext();
  const [hovering, setHovering] = useState(false);
  const [loading, setLoading] = useState(false);

  const ltiConsumerEndpoint = `${config.apiUrl}/${ltiEndpoint}`;

  // validate entity
  if (element.entity == null) return null;
  const { entity: entityId } = element;
  const selectedEntity = entities[entityId];
  if (selectedEntity?.type !== 'lti') return null;

  const { linkId, params, failed } = selectedEntity;
  const { spaceId, sessionToken, userId } = config;
  const { Delete } = icons;

  const onDelete = () => {
    Transforms.delete(editor, {
      at: ReactEditor.findPath(editor, element),
      voids: true,
    });
    ReactEditor.focus(editor);
  };

  /**
   * Obtains the lti request data from generic-lti lambda,
   */
  const getLtiReq = async () => {
    try {
      setLoading(true);
      const result = await fetch(ltiConsumerEndpoint, {
        method: 'post',
        body: JSON.stringify({
          ...(params || {}),
          spaceId,
          linkId,
        }),
        headers: {
          'Content-Type': 'application/json',
          'x-session-token': sessionToken,
          'x-user-id': userId,
        },
      });

      setLoading(false);

      // if there is an error message in payload,
      //  then mark the entity as failed
      const request = await result.json();
      if (request?.errors?.length > 0) {
        // eslint-disable-next-line no-console
        console.error(request.errors[0]);
        updateEntity(entityId, { failed: true });
        return null;
      }

      if (request.message) {
        // eslint-disable-next-line no-console
        console.error(request.message);
        updateEntity(entityId, { failed: true });
        return null;
      }

      return request;
    } catch (ex) {
      setLoading(false);
      // eslint-disable-next-line no-console
      console.error(ex);
      updateEntity(entityId, { failed: true });
      return null;
    }
  };

  /**
   * 1 - Obtains the lti request data via getLtiReq
   * 2 - Constructs the authenticated URL to LTI provider URL so that login page is bypassed
   * 3 - Returns the authenticated URL
   */
  const getLaunchFormUrl = async () => {
    // obtain lti provider request
    const request = await getLtiReq();
    if (!request) {
      return null;
    }

    const { body, url: ltiLaunchUrl } = request;
    const bodyEncoded = encodeURIComponent(JSON.stringify(body));
    const urlEncoded = encodeURIComponent(ltiLaunchUrl);

    // construct authenticated URL
    const launchFormUrlBase = `${config.apiUrl}/lti-launchform`;
    return `${launchFormUrlBase}?body=${bodyEncoded}&url=${urlEncoded}`;
  };

  /**
   * Obtains the authenticated URL to LTI 1.1 provider and opens the URL in a new tab
   */
  const onClick = async () => {
    const launchFormUrl = await getLaunchFormUrl();
    if (launchFormUrl) {
      window.open(launchFormUrl);
    }
  };

  if (loading) {
    return (
      <LoadingContainer contentEditable={false}>
        <Loading />
      </LoadingContainer>
    );
  }

  const toolbarItems: BlockToolbarItem[] = [
    {
      title: 'Delete',
      isVisible: !isReadOnly,
      icon: Delete,
      onAction: onDelete,
    },
  ];

  const blockToolbar = (
    <BlockToolbar isVisible={isSelected || hovering} items={toolbarItems} />
  );

  if (failed) {
    const onRetry = () => updateEntity(entityId, { failed: false });
    return (
      <Failed
        isSelected={isSelected}
        data-testid="failed-generic-lti-link"
        contentEditable={false}
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        aria-current={isSelected}
      >
        {blockToolbar}
        <FailedIcon>
          <icons.ErrorIcon />
        </FailedIcon>
        <FailedDescription>
          {title} content failed to load.
          {!isReadOnly && (
            <>
              <br />
              <BlockButton onClick={onRetry}>Retry</BlockButton>
            </>
          )}
        </FailedDescription>
      </Failed>
    );
  }

  return (
    <LtiLinkBlockWrapper
      onClick={onClick}
      tabIndex={-1}
      aria-label={title}
      data-testid="lti-link-block"
      isSelected={isSelected}
      aria-current={isSelected}
      contentEditable={false}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      <BlockLinkWrapper contentEditable={false}>
        <BlockLinkContent>
          <Header>
            <ImageWrapper>
              <img src={ltiIcon} alt="" height="24px" />
            </ImageWrapper>
            <Title>{title}</Title>
          </Header>
          {blockToolbar}
        </BlockLinkContent>
      </BlockLinkWrapper>
    </LtiLinkBlockWrapper>
  );
};
